import type { ComponentType, SVGProps } from 'react';

type TrustBadge = {
  title: string;
  metric: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
};

type HomeTrustStripProps = Readonly<{
  badges: readonly TrustBadge[];
  variant?: 'light' | 'dark';
}>;

/**
 * Single-line trust row — one placement per funnel, not repeated card grids.
 */
export function HomeTrustStrip({ badges, variant = 'light' }: HomeTrustStripProps) {
  const isDark = variant === 'dark';

  return (
    <div
      className={
        isDark
          ? 'mt-10 border-t border-white/10 pt-6'
          : 'border-t border-slate-200/80 pt-8'
      }
    >
      <p
        className={
          isDark
            ? 'text-xs font-semibold uppercase tracking-wide text-white/50'
            : 'text-xs font-semibold uppercase tracking-wide text-slate-500'
        }
      >
        Checkout confidence
      </p>
      <ul className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {badges.map((b) => {
          const Icon = b.icon;
          return (
            <li
              key={b.title}
              className={
                isDark
                  ? 'flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-3'
                  : 'flex items-center gap-3 rounded-2xl border border-slate-200/70 bg-white px-3 py-3 shadow-sm'
              }
            >
              <span
                className={
                  isDark
                    ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10'
                    : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100/70'
                }
              >
                <Icon className={`h-4 w-4 ${isDark ? 'text-indigo-200' : 'text-indigo-700'}`} />
              </span>
              <span className="min-w-0">
                <span
                  className={`block text-xs font-bold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {b.title}
                </span>
                <span
                  className={`block text-[11px] truncate ${isDark ? 'text-white/60' : 'text-slate-600'}`}
                >
                  {b.metric}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
